import { useSelector } from '@legendapp/state/react';
import { getAccountTasks } from '@tf/shared';
import { useMemo } from 'react';

import { useSelectedAccount } from '@/core/hooks/use-selected-account';
import { appStore } from '@/core/stores';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

import { useAccountInfoSegments } from './use-account-info-segments';

export const useSelectedAccountTasks = () => {
	const canEditAccounts = useCurrentUserCapabilityCheck(UserCapabilities.CAN_PREPARE_ACCOUNTS_DATA);
	const canReviewAccounts = useCurrentUserCapabilityCheck(UserCapabilities.CAN_REVIEW_ACCOUNTS_DATA);

	const account = useSelectedAccount();
	const { nextKycReviewDate } = useAccountInfoSegments();
	const formDefs = useSelector(() => appStore.defs.get().forms);
	const entityDefs = useSelector(() => appStore.defs.get().entities);

	const accountTasks = useMemo(() => {
		const reviews = account.reviews.sort((a, b) => a.connectionKind.localeCompare(b.connectionKind));

		return getAccountTasks({
			formDefs,
			entityDefs,
			accountId: account.meta.graphNodeId,
			reviews,
			nextKycReviewDate: nextKycReviewDate,
		});
	}, [formDefs, entityDefs, account.meta.graphNodeId, account.reviews, nextKycReviewDate]);

	return accountTasks.map((reviewTasks) => {
		return {
			...reviewTasks,
			entities: reviewTasks.entities.map((entityTasks) => {
				return {
					...entityTasks,
					tasks: entityTasks.tasks.filter((task) => {
						switch (task.type) {
							case 'PENDING_FILL':
							case 'VALIDATION_ERRORS':
							case 'REJECTED':
							case 'REQUIRED_CONNECTION':
							case 'CORRECT_SHAREHOLDERS_PERCENTAGE_SUM':
							case 'CORRECT_PARTNERSHIP_PERCENTAGE_SUM':
								return canEditAccounts;
							case 'PENDING_REVIEW':
							case 'FINALIZE_REVIEW':
							case 'KYC_FINALIZE_REVIEW':
								return canReviewAccounts;
							default:
								throw new Error(`Unhandled task type in capabilities filter`);
						}
					}),
				};
			}),
		};
	});
};
